import type { FormData as ApiFormData, OrderCra } from "@ifa/utils";

export const ZUU_CRA_FIELD_ALIASES = {
  hasVerifiedLatestIdentityDocument: [
    "zuuIdentityVerified",
    "validIdentityDocuments",
  ],
  customerResidenceRiskLevel: ["zuuCountryRiskLevel"],
  customerIndustryRiskLevel: ["zuuOccupationRiskLevel"],
  customerFundsFromHongKong: ["zuuFundsFromHongKong"],
  customerSourceOfWealth: ["zuuWealthSource"],
  customerSourceOfWealth_F_remark: ["zuuWealthSourceOther"],
  initialPremiumPaymentMethod: ["zuuTransactionPrimaryMethods"],
  renewalPremiumPaymentMethod: ["zuuTransactionSettlementMethods"],
  expectedAnnualTransactionCount: ["zuuTransactionVolume"],
  qualifyingLifeInsurancePolicy: [
    "zuuQualifyingLifePolicy",
    "exceedsHighPremiumThreshold",
  ],
  hasInsurableInterest: ["zuuInsurableInterest"],
  hasPepOrTerroristConnection: ["pep"],
} as const;

export const ZUU_CRA_RADIO_FIELDS = [
  "hasVerifiedLatestIdentityDocument",
  "customerResidenceRiskLevel",
  "customerIndustryRiskLevel",
  "customerFundsFromHongKong",
  "initialPremiumPaymentMethod",
  "renewalPremiumPaymentMethod",
  "expectedAnnualTransactionCount",
  "qualifyingLifeInsurancePolicy",
  "hasInsurableInterest",
  "hasPepOrTerroristConnection",
] as const;

export const ZUU_CRA_CHECKBOX_FIELDS = [
  "customerSourceOfWealth",
] as const;

export const ZUU_CRA_INPUT_FIELDS = [
  "customerSourceOfWealth_F_remark",
] as const;

export type ZuuCraRadioField = (typeof ZUU_CRA_RADIO_FIELDS)[number];
export type ZuuCraCheckboxField = (typeof ZUU_CRA_CHECKBOX_FIELDS)[number];
export type ZuuCraInputField = (typeof ZUU_CRA_INPUT_FIELDS)[number];
export type ZuuCraField = ZuuCraRadioField | ZuuCraCheckboxField | ZuuCraInputField;

export const ZUU_CRA_FIELDS: ZuuCraField[] = [
  ...ZUU_CRA_RADIO_FIELDS,
  ...ZUU_CRA_CHECKBOX_FIELDS,
  ...ZUU_CRA_INPUT_FIELDS,
];

export const ZUU_CRA_LEGACY_QUESTION_KEYS = new Set(
  Object.values(ZUU_CRA_FIELD_ALIASES).flat(),
);

const ZUU_WEALTH_SOURCE_LEGACY_VALUE_MAP: Record<string, string> = {
  SALARY: "A",
  SAVINGS: "B",
  INVESTMENT: "C",
  INHERITANCE: "D",
  COMPANY_PROFIT: "E",
  OTHER: "F",
};

const ZUU_TRANSACTION_METHOD_LEGACY_VALUE_MAP: Record<string, string> = {
  CHECK_CARD: "A",
  BANK_DEPOSIT_ATM_INSURER: "B",
  BANK_TRANSFER: "C",
  ONLINE_TRANSFER_FPS: "D",
  THIRD_PARTY: "E",
  CASH: "F",
};

function normalizeChoiceValues(
  field: ZuuCraRadioField | ZuuCraCheckboxField,
  rawValue: any,
) {
  if (rawValue === undefined || rawValue === null || rawValue === "") {
    return undefined;
  }

  const values = Array.isArray(rawValue) ? rawValue : [rawValue];
  const normalizedValues = values
    .map((value) => {
      if (field === "customerSourceOfWealth") {
        return ZUU_WEALTH_SOURCE_LEGACY_VALUE_MAP[value] || value;
      }

      if (
        field === "initialPremiumPaymentMethod" ||
        field === "renewalPremiumPaymentMethod"
      ) {
        return ZUU_TRANSACTION_METHOD_LEGACY_VALUE_MAP[value] || value;
      }

      return value;
    })
    .filter((value) => typeof value === "string" && value.trim().length > 0);

  return normalizedValues;
}

function normalizeFieldValue(field: ZuuCraField, rawValue: any) {
  if ((ZUU_CRA_INPUT_FIELDS as readonly string[]).includes(field)) {
    return rawValue ?? undefined;
  }

  const normalizedValues = normalizeChoiceValues(
    field as ZuuCraRadioField | ZuuCraCheckboxField,
    rawValue,
  );
  if (!normalizedValues || normalizedValues.length === 0) {
    return undefined;
  }

  if ((ZUU_CRA_CHECKBOX_FIELDS as readonly string[]).includes(field)) {
    return normalizedValues;
  }

  return normalizedValues[0];
}

function extractQuestionValue(item: ApiFormData, field: ZuuCraField) {
  if ((ZUU_CRA_INPUT_FIELDS as readonly string[]).includes(field)) {
    if (item.value !== undefined && item.value !== null) {
      return String(item.value);
    }

    const rawInputValue = item.items?.find(
      (option) =>
        typeof option.value === "string" || typeof option.value === "number",
    )?.value;
    if (rawInputValue !== undefined && rawInputValue !== null) {
      return String(rawInputValue);
    }
    return undefined;
  }

  if (item.type === "RADIO") {
    const selectedItem = item.items?.find((option) => option.value === true);
    return normalizeFieldValue(field, selectedItem?.key);
  }

  if (item.type === "CHECKBOX") {
    const selectedItems =
      item.items
        ?.filter((option) => option.value === true)
        .map((option) => option.key) || [];
    return normalizeFieldValue(field, selectedItems);
  }

  if (item.value !== undefined && item.value !== null) {
    return normalizeFieldValue(field, item.value);
  }

  const checkedItems =
    item.items?.filter((option) => option.value === true).map((option) => option.key) ||
    [];
  if (checkedItems.length > 0) {
    return normalizeFieldValue(field, checkedItems);
  }

  const rawChoiceValue = item.items?.find(
    (option) =>
      typeof option.value === "string" || typeof option.value === "number",
  )?.value;
  if (rawChoiceValue !== undefined && rawChoiceValue !== null) {
    return normalizeFieldValue(field, rawChoiceValue);
  }

  return undefined;
}

export function getZuuCraFieldValue(
  craData: Pick<OrderCra, "answer"> & Record<string, any>,
  field: ZuuCraField,
) {
  const aliases = [field, ...(ZUU_CRA_FIELD_ALIASES[field] || [])];

  if (Array.isArray(craData.answer)) {
    for (const key of aliases) {
      const item = craData.answer.find((question) => question.question === key);
      if (!item) continue;

      const value = extractQuestionValue(item, field);
      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }
  }

  for (const key of aliases) {
    const rawValue = craData[key];
    const value = normalizeFieldValue(field, rawValue);
    if (
      value !== undefined &&
      value !== null &&
      value !== "" &&
      (!Array.isArray(value) || value.length > 0)
    ) {
      return value;
    }
  }

  return undefined;
}
